import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import ActivityWrapper from "./activity-wrapper";
import ActivityIconWrapper from "./activity-icon-wrapper";
import ActivityContentWrapper from "./activity-content-wrapper";
import { ActivityItemHeader } from "./activity-item-header";
import useTranslation, {
  Translation,
} from "@babackapp/translations/use-translation";
import shippingBoLogo from "@/assets/integrations/shippingbo-icon.png";

type SboStockReservationActivityProps = {
  activity: ReturnRequestLogDto;
};

export default function SboStockReservationActivity({
  activity,
}: SboStockReservationActivityProps) {
  const t = useTranslation();

  const params = activity.params as {
    action: "create" | "cancel";
    sboOrderId?: number;
    requestBody?: string;
    response?: string;
    error?: string;
  };

  const isError = !!params.error;

  const formatJson = (value: string | undefined): string | undefined => {
    if (value === undefined) return undefined;
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  };

  const formattedRequest = formatJson(params.requestBody);
  const formattedResponse = formatJson(params.response);

  return (
    <ActivityWrapper>
      <ActivityIconWrapper
        className={`${isError ? "text-red-500 bg-red-100" : ""}`}
      >
        <img src={shippingBoLogo} className="size-4" alt="ShippingBo logo" />
      </ActivityIconWrapper>
      <ActivityContentWrapper>
        <div className="flex items-center gap-2 flex-1 justify-between w-full min-w-0">
          <Accordion type="single" collapsible className="flex-1 min-w-0 w-full">
            <AccordionItem value="item-1" className="border-0">
              <AccordionTrigger className="p-0 w-full min-w-0">
                <ActivityItemHeader
                  title=""
                  timestamp={activity.date}
                  description={null}
                >
                  <div className="flex flex-col gap-0">
                    <div className="flex items-center gap-2">
                      <span>
                        ShippingBo:{" "}
                        {t(
                          `activities.shippingBoReservation.${params.action}` as Translation,
                        )}
                        {isError && ` - ${t("misc.error")}`}
                      </span>
                    </div>
                  </div>
                </ActivityItemHeader>
              </AccordionTrigger>
              <AccordionContent>
                {params.sboOrderId !== undefined && (
                  <div className="text-xs text-muted-foreground mt-2">
                    {t("activities.shippingBoReservation.orderId" as Translation)}
                    : {params.sboOrderId}
                  </div>
                )}
                {params.error && (
                  <div className="text-sm text-red-600 mt-2">
                    <span className="font-medium">{t("misc.error")} : </span>
                    {params.error}
                  </div>
                )}
                {formattedRequest && (
                  <div className="mt-2">
                    <span className="text-xs font-medium">
                      {t("activities.shippingBoReservation.request" as Translation)}
                    </span>
                    <pre className="mt-1 rounded-md bg-muted p-4 text-xs overflow-x-auto whitespace-pre-wrap">
                      {formattedRequest}
                    </pre>
                  </div>
                )}
                {formattedResponse && (
                  <div className="mt-2">
                    <span className="text-xs font-medium">
                      {t("activities.shippingBoReservation.response" as Translation)}
                    </span>
                    <pre className="mt-1 rounded-md bg-muted p-4 text-xs overflow-x-auto whitespace-pre-wrap">
                      {formattedResponse}
                    </pre>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </ActivityContentWrapper>
    </ActivityWrapper>
  );
}
